import {
  Button,
  Collapse,
  createStyles,
  Dialog,
  DialogContent,
  DialogTitle,
  makeStyles,
  Tab,
  Tabs,
  Theme,
  Typography,
} from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useMapService from "../../hooks/MapServiceHooks/useMapService";
import {
  ILatLng,
  selectActiveGroupMembers,
  selectCleverlynkGroup,
  selectCompany,
  selectIsMobile,
  selectOpenGeo,
  selectTriggerNavToUserForm,
} from "../../redux/slices/cleverlynkSlice";
import HistoricAddress from "../DeliveryMapService/HistoricAddresses";
import { ArrowLeft } from "@material-ui/icons";
import useCleverlynkFunctions from "../../hooks/useCleverlynkFunctions";
import useTemplateStyles from "../../hooks/useTemplateStyles";
import NoCoverageDialog from "./NoCoverageDialog";
import useUserFormFunctions from "../../hooks/useUserFormFunctions";
import { PFXCriteriaType } from "../../models/Interfaces";
import {
  QueryState,
  selectDeliveryCookiesAmount,
  selectDeliveryInfo,
  selectGetUserLocationSupervisor,
} from "../../redux/slices/mapServiceSlice";
import useDeliveryAddressCookies, {
  IDeliveryAddressCookie,
} from "../../hooks/MapServiceHooks/useDeliveryAddressCookies";
import AddAddress from "./AddAddress";
import CoverageSelector from "./CoverageSelector";
import FirstTimeQuery from "./FirstTimeQuery";

import { useTranslation } from "react-i18next";
import "../../i18n/config";

export interface ISelectedCleverlynk {
  id: string;
  navigateToForm: boolean;
}

export enum GeoSelectorStage {
  "COVERAGE",
  "HISTORIC",
  "ADD_ADDRESS",
  "QUERY_WITH_ADDRESS",
}

interface IGeoSelector {}

const GeoSelector = () => {
  const [stage, setStage] = useState<GeoSelectorStage>(GeoSelectorStage.QUERY_WITH_ADDRESS);
  const [imageWidth, setImageWidth] = useState<number>(200);
  const [noCoverage, setNoCoverage] = useState<boolean>(false);
  const [collapseBanner, setCollapseBanner] = useState<boolean>(false);

  const openGeo = useSelector(selectOpenGeo);
  const cleverlynkGroup = useSelector(selectCleverlynkGroup);
  const activeGroupMembers = useSelector(selectActiveGroupMembers);
  const company = useSelector(selectCompany);
  const isMobile = useSelector(selectIsMobile);
  const deliveryInfo = useSelector(selectDeliveryInfo);
  const getUserLocationSupervisor = useSelector(selectGetUserLocationSupervisor);
  const cookiesAmount = useSelector(selectDeliveryCookiesAmount);
  const navigateToForm = useSelector(selectTriggerNavToUserForm);

  const { t } = useTranslation(["groups", "deliveryinput"]);

  const { primaryColor } = useTemplateStyles();

  const { handleModifyOpenGeo, handleRedirectToClynk, handleSetConfirmLocationLater } = useCleverlynkFunctions();

  const { handleAddCookie } = useDeliveryAddressCookies();

  const {
    isMarkerInsidePolygon,
    isMarkerInsideCircle,
    calculateDistance,
    handleModifyDeliveryInfo,
    handleGetUserLocation,
    handleSetSelectedAddress,
  } = useMapService();

  useEffect(() => {
    if (getUserLocationSupervisor.status !== "succeeded" && openGeo) handleGetUserLocation();
  }, [openGeo]);

  useEffect(() => {
    if (cookiesAmount > 0) setStage(GeoSelectorStage.HISTORIC);
    else setStage(GeoSelectorStage.QUERY_WITH_ADDRESS);
  }, [cookiesAmount]);

  useEffect(() => {
    switch (stage) {
      case GeoSelectorStage.QUERY_WITH_ADDRESS:
        if (collapseBanner) setCollapseBanner(true);
        else setCollapseBanner(false);
        break;
      case GeoSelectorStage.ADD_ADDRESS:
      case GeoSelectorStage.COVERAGE:
        setCollapseBanner(true);
        break;
      default:
        setCollapseBanner(false);
        break;
    }
  }, [collapseBanner, stage]);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: "100%",
        maxWidth: 550,
        margin: isMobile && stage !== GeoSelectorStage.COVERAGE && stage !== GeoSelectorStage.ADD_ADDRESS ? 0 : 4,
        maxHeight: isMobile ? "90%" : "calc(100% - 20px)",
        position: "relative",
        marginTop: -20,
        borderRadius: 9,
      },
      dialogContainer: {
        zIndex: 1,
        transitionProperty: "height",
        transitionDuration: "400ms",
      },
      content: {
        padding: 16,
        paddingTop: 6,
      },
      closeButton: {
        position: "absolute",
        left: theme.spacing(1),
        top: theme.spacing(1),
        color: stage === GeoSelectorStage.ADD_ADDRESS ? "#1a1a1a" : "#00000000",
      },
      dialogTitle: {
        backgroundColor: stage === GeoSelectorStage.ADD_ADDRESS ? "#fff" : `${primaryColor ?? "#ffffff"}26`,
        paddingTop: stage === GeoSelectorStage.ADD_ADDRESS ? 40 : 10,
        paddingBottom: stage === GeoSelectorStage.ADD_ADDRESS ? "0px" : 10,
        transitionDuration: "400ms",
        transitionProperty: "background-color, padding-top",
      },
      laterDiv: {
        // marginBottom: -20,
        justifyContent: "center",
        alignContent: "flex-end",
        display: "flex",
        position: "fixed",
        width: "100%",
        bottom: 5,
        zIndex: 4999,
      },
      laterButton: {
        textTransform: "none",
        fontSize: "0.8rem",
        textDecoration: "underline",
        color: "white",
      },
    })
  );

  const classes = useStyles();

  const toggleFocus = (focus: boolean) => {
    setCollapseBanner(focus);
  };

  const handleClose = () => {
    handleSetConfirmLocationLater(true);
    handleModifyOpenGeo(false);
  };

  const getClynkIdWithinRange = (selectedAddressLocation: ILatLng) => {
    let distance = 20000000;
    let index = -1;
    const openActiveMembers = activeGroupMembers.filter(a => a.open);
    for (let i = 0; i < openActiveMembers.length; i++) {
      let isInside = false;

      if (openActiveMembers[i].geolocationInfo?.polyType === "circle")
        isInside = isMarkerInsideCircle(
          {
            lat: openActiveMembers[i].geolocationInfo?.location!.lat!,
            lng: openActiveMembers[i].geolocationInfo?.location!.lng!,
          },
          openActiveMembers[i].geolocationInfo?.radius!,
          selectedAddressLocation!
        );
      else
        isInside = isMarkerInsidePolygon(
          openActiveMembers[i].geolocationInfo?.polygon!.map(p => ({ lat: p.lat!, lng: p.lng! })) || [],
          selectedAddressLocation!
        );

      if (isInside) {
        const newDistance = calculateDistance(
          {
            lat: openActiveMembers[i].geolocationInfo?.location!.lat!,
            lng: openActiveMembers[i].geolocationInfo?.location!.lng!,
          },
          selectedAddressLocation!
        );
        if (newDistance < distance) {
          distance = newDistance;
          index = i;
        }
      }
    }

    if (index > -1) return openActiveMembers[index].id;
    else return undefined;
  };

  const handleClynkForwardByLocation = useCallback(
    (location: ILatLng | undefined, dInfo?: Partial<QueryState>) => {
      if (!location) return;
      const id = getClynkIdWithinRange({ lat: location?.lat!, lng: location?.lng! });
      if (!id) {
        setNoCoverage(true);
      } else {
        // console.log({ prev: dInfo?.coordinates, next: location });
        if (dInfo) handleAddCookie({ ...dInfo, coordinates: location });
        else {
          handleAddCookie({ ...deliveryInfo, coordinates: location });
        }

        handleSetSelectedAddress(true);
        handleRedirectToClynk({
          id,
          navigateToForm,
        });
      }
    },
    [deliveryInfo, navigateToForm, handleRedirectToClynk]
  );

  const handleClynkForwardByCookies = useCallback(
    (a: IDeliveryAddressCookie) => {
      const id = getClynkIdWithinRange({ lat: a.coordinates?.lat!, lng: a.coordinates?.lng! });
      handleModifyDeliveryInfo({
        suggestions: [],
        address: a.address,
        byAddress: false,
        city: a.city,
        country: a.country,
        coordinates: a.coordinates,
        propertyInfo: a.propertyInfo,
        additionalInfo: a.additionalInfo,
      });
      if (!id) {
        setNoCoverage(true);
      } else {
        handleSetSelectedAddress(true);
        handleRedirectToClynk({
          id,
          navigateToForm,
        });
      }
    },
    [getClynkIdWithinRange, handleModifyDeliveryInfo, handleRedirectToClynk]
  );

  const handleClynkIdOnConfirm = useCallback(
    (id: string) => {
      handleRedirectToClynk({
        id,
        navigateToForm,
      });
    },
    [navigateToForm, handleRedirectToClynk]
  );

  const renderStage = useMemo(() => {
    switch (stage) {
      case GeoSelectorStage.COVERAGE:
        return <CoverageSelector onConfirm={handleClynkIdOnConfirm} />;

      case GeoSelectorStage.HISTORIC:
        return (
          <HistoricAddress
            onConfirm={handleClynkForwardByCookies}
            toggleAddition={() => setStage(GeoSelectorStage.ADD_ADDRESS)}
          />
        );
      case GeoSelectorStage.QUERY_WITH_ADDRESS:
        return <FirstTimeQuery onConfirm={handleClynkForwardByLocation} toggleFocus={() => toggleFocus(true)} />;

      case GeoSelectorStage.ADD_ADDRESS:
        return <AddAddress toggleHistoric={() => setStage(GeoSelectorStage.HISTORIC)} />;

      default:
        return null;
    }
  }, [
    deliveryInfo,
    getClynkIdWithinRange,
    handleAddCookie,
    handleModifyDeliveryInfo,
    handleRedirectToClynk,
    handleSetSelectedAddress,
    navigateToForm,
    stage,
  ]);

  const dialogSubtitle = useMemo(() => {
    switch (cleverlynkGroup?.type) {
      case "GEO":
        switch (stage) {
          case GeoSelectorStage.COVERAGE:
            return <Typography style={{ fontSize: "0.85rem" }}>{t("groups:selectInMap")}</Typography>;
          case GeoSelectorStage.HISTORIC:
            return <Typography style={{ fontSize: "0.85rem" }}>{t("groups:addOrChooseAddress")}</Typography>;
          default:
            break;
        }
        break;
      default:
        return <Typography style={{ fontSize: "0.85rem" }}>{cleverlynkGroup?.title ?? "Elige tu zona"}</Typography>;
    }
  }, [cleverlynkGroup?.title, cleverlynkGroup?.type, stage]);

  const tabs = () => (
    <Tabs value={stage} onChange={(e, v) => setStage(v)} centered>
      {cookiesAmount === 0 ? (
        <Tab
          value={GeoSelectorStage.QUERY_WITH_ADDRESS}
          label={<Typography style={{ textTransform: "none" }}>{t("groups:searchAddress")}</Typography>}
        />
      ) : (
        <Tab
          value={GeoSelectorStage.HISTORIC}
          label={<Typography style={{ textTransform: "none" }}>{t("groups:myAddresses")}</Typography>}
        />
      )}
      <Tab
        value={GeoSelectorStage.COVERAGE}
        label={<Typography style={{ textTransform: "none" }}>{t("groups:chooseByArea")}</Typography>}
      />
    </Tabs>
  );

  return (
    <>
      <Dialog
        open={openGeo}
        classes={{ paper: classes.root }}
        fullScreen={isMobile && cleverlynkGroup?.type === "GEO"}
        onClose={handleClose}>
        <DialogTitle className={classes.dialogTitle} disableTypography>
          {stage !== GeoSelectorStage.ADD_ADDRESS ? (
            <React.Fragment>
              <Typography variant="h6">
                {t("groups:welcomeTo")} {company?.name}
              </Typography>
              {dialogSubtitle}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Typography variant="h6" id="add-address">
                {t("deliveryinput:historicAddresses.addAnAddress")}
              </Typography>
              <Typography style={{ fontSize: "0.85rem" }}>
                {t("deliveryinput:historicAddresses.willBeSavedInDevice")}
              </Typography>
            </React.Fragment>
          )}
          <Collapse in={stage === GeoSelectorStage.ADD_ADDRESS} timeout={400}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={() => setStage(GeoSelectorStage.HISTORIC)}
              aria-label="close"
              className={classes.closeButton}>
              <Typography variant="caption" style={{ textDecoration: "underline" }}>
                {t("groups:back")}
              </Typography>
            </Button>
          </Collapse>
        </DialogTitle>
        {cleverlynkGroup?.image !== undefined && (
          <Collapse in={!collapseBanner} style={{ overflow: "hidden" }}>
            <img
              id="group-banner-img"
              style={{ width: "100%", height: (imageWidth / 4) * 2.5, objectFit: "cover" }}
              src={cleverlynkGroup?.image}
              alt="Banner"
            />
          </Collapse>
        )}
        <DialogContent
          id="group-selector-content"
          className={classes.content}
          ref={(elem: any) => (elem && imageWidth === 200 ? setImageWidth(elem.scrollWidth - 32) : {})}>
          {stage !== GeoSelectorStage.ADD_ADDRESS && tabs()}
          {renderStage}
          {cookiesAmount > 0 && stage === GeoSelectorStage.HISTORIC && (
            <div
              style={{
                position: "absolute",
                bottom: 0,
                backgroundColor: "white",
                height: 55,
                padding: "5px 15px 5px 15px",
                zIndex: 120,
                left: 0,
                boxShadow: "0pt -1pt 2px #eaeaea",
              }}>
              <Typography variant="caption" style={{ color: "#848484" }}>
                {t("deliveryinput:historicAddresses.disclaimer")}
              </Typography>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <NoCoverageDialog
        open={noCoverage}
        cancel={() => {
          setNoCoverage(false);
        }}
      />
      {openGeo && (
        <div className={classes.laterDiv}>
          <Button onClick={handleClose} className={classes.laterButton}>
            {t("groups:confirmLater")}
          </Button>
        </div>
      )}
    </>
  );
};
export default GeoSelector;
